<template>
  <v-card height="90%" class="d-flex flex-column">
    <v-card-title class="subtitle-1 py-2 primary white--text">
      AI Styling Assistant
      <v-spacer />
      <v-btn
        icon
        small
        dark
        @click="chatHistory = []"
        :disabled="chatHistory.length === 0"
      >
        <v-icon small>mdi-delete-outline</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text
      ref="messageList"
      class="flex-grow-1 overflow-y-auto ai-chat-messages pa-2"
    >
      <div v-if="chatHistory.length === 0" class="text-center grey--text mt-4">
        <v-icon large color="grey lighten-1">mdi-robot</v-icon>
        <p class="mt-2">
          Describe styling changes in plain English.<br />e.g. "Make all
          headings blue"
        </p>
      </div>
      <div v-for="(msg, i) in chatHistory" :key="i" class="mb-2">
        <div
          class="chat-bubble"
          :class="
            msg.role === 'user' ? 'chat-bubble--user' : 'chat-bubble--assistant'
          "
        >
          {{ msg.content }}
        </div>
      </div>
      <div v-if="loading" class="mb-2">
        <div class="chat-bubble chat-bubble--assistant">
          <v-progress-circular
            indeterminate
            size="16"
            width="2"
            color="primary"
          />
          Thinking…
        </div>
      </div>
    </v-card-text>
    <v-divider />
    <v-card-actions class="pa-2">
      <v-text-field
        v-model="inputText"
        placeholder="Type a styling request…"
        dense
        outlined
        hide-details
        :disabled="loading"
        @keyup.enter="sendMessage"
      />
      <v-btn
        icon
        color="primary"
        :disabled="loading || !inputText.trim()"
        @click="sendMessage"
        class="ml-1"
      >
        <v-icon>mdi-send</v-icon>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import gql from "graphql-tag";

const AI_STYLE_DOCUMENT = gql`
  mutation aiStyleDocument($input: AiStyleDocumentInput!) {
    aiStyleDocument(input: $input) {
      reply
      css
      success
      error
    }
  }
`;

export default {
  name: "document-ai-style-chat",

  props: {
    documentId: {
      type: String,
      required: true,
    },
    previewUrl: {
      type: String,
      default: () => "",
    },
  },

  data() {
    return {
      chatHistory: [],
      inputText: "",
      loading: false,
    };
  },

  methods: {
    async sendMessage() {
      const text = this.inputText.trim();
      if (!text || this.loading) return;

      this.chatHistory.push({ role: "user", content: text });
      this.inputText = "";
      this.loading = true;
      this.$nextTick(this.scrollToBottom);

      const history = this.chatHistory.slice(0, -1).map((m) => ({
        role: m.role,
        content: m.content,
      }));

      try {
        const { data } = await this.$apollo.mutate({
          mutation: AI_STYLE_DOCUMENT,
          variables: {
            input: {
              documentId: this.documentId,
              content: text,
              history,
              previewUrl: this.previewUrl,
            },
          },
        });

        const result = data.aiStyleDocument;
        if (result.success) {
          this.chatHistory.push({
            role: "assistant",
            content: result.reply || "Done! CSS has been updated.",
          });
          this.$root.$emit("refresh-preview");
          if (result.css) this.$emit("css-updated", result.css);
        } else {
          this.chatHistory.push({
            role: "assistant",
            content: `Error: ${result.error || "Something went wrong."}`,
          });
        }
      } catch (err) {
        const msg = err.graphQLErrors
          ? err.graphQLErrors.map((e) => e.message).join(", ")
          : err.message;
        this.chatHistory.push({ role: "assistant", content: `Error: ${msg}` });
      } finally {
        this.loading = false;
        this.$nextTick(this.scrollToBottom);
      }
    },

    scrollToBottom() {
      const el = this.$refs.messageList;
      if (el) el.scrollTop = el.scrollHeight;
    },
  },
};
</script>

<style scoped>
.ai-chat-messages {
  max-height: calc(100vh - 280px);
}
.chat-bubble {
  display: inline-block;
  max-width: 90%;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 0.875rem;
  line-height: 1.4;
  word-break: break-word;
  margin-bottom: 10px;
}
.chat-bubble--user {
  background-color: #1976d2;
  color: #fff;
  float: right;
  clear: both;
}
.chat-bubble--assistant {
  background-color: #f5f5f5;
  color: #333;
  float: left;
  clear: both;
}
</style>



// WEBPACK FOOTER //
// src/components/gt/admin/documents/DocumentAiStyleChat.vue